/**
 * /photo-recolor — tap an object, give it a colour; folds, shadows and gloss stay.
 *
 * The colour arithmetic and every constant behind it are the shared «object
 * paint» engine (public/js/object-paint/, docs/why/primitives/object-paint.md),
 * measured on real photos on 21.10.2026. This file is the page: picking with
 * EdgeTAM (the same worker /remove-object-from-video drives, through the shared
 * EdgeTamClient), the preview, the reference photo, the variants and the export.
 *
 * Where the work happens:
 *   · selection — EdgeTAM in ro-worker (EdgeTamClient), on the WORKING copy
 *     (≤ 1280 px long side);
 *   · analysis of an object, its weights, the reference region, the export
 *     strips — op-worker.js;
 *   · the preview paint — here, at the working size, from cached weights: a
 *     colour change is arithmetic only and lands in tens of milliseconds.
 * The export paints the FULL-size photo strip by strip with the same paint
 * function the preview used, so what was seen is what is saved.
 */
(function (root) {
    'use strict';

    var WORK_LONG = 1280;          // the working copy: selection, analysis, preview
    var STRIP = 256;               // export rows per worker round-trip
    var MAX_OBJECTS = 12;
    var MAX_VARIANTS = 12;

    function $(id) { return document.getElementById(id); }
    function on(el, ev, fn, o) { if (el) el.addEventListener(ev, fn, o || false); }
    function show(el, v) { if (el) el.hidden = !v; }
    function feat(f, p) { try { root.__aeFeature && root.__aeFeature(f, p || {}); } catch (_) { } }
    function stage(n) { try { root.__stage && root.__stage(n); } catch (_) { } }
    function toast(msg, type) { if (msg && root.__toast) root.__toast(msg, type || 'info'); }
    function fmt(s, map) { s = String(s || ''); for (var k in map) s = s.split(':' + k).join(map[k]); return s; }

    var SLUG, T, CFG;
    var S = null;                  // the session for the current photo
    var W = null, wseq = 0, wpend = new Map();   // op-worker
    var SEG = null;                // EdgeTamClient

    /* ── op-worker ───────────────────────────────────────────────────────── */
    function wcall(msg, transfer, onProgress) {
        return new Promise(function (resolve, reject) {
            msg.id = ++wseq;
            wpend.set(msg.id, { resolve: resolve, reject: reject, onProgress: onProgress });
            W.postMessage(msg, transfer || []);
        });
    }
    function ensureWorker() {
        if (W) return Promise.resolve();
        W = new Worker(CFG.opWorkerUrl);
        W.onmessage = function (ev) {
            var m = ev.data, p = wpend.get(m.id);
            if (!p) return;
            if (m.type === 'progress') { if (p.onProgress) p.onProgress(m.p); return; }
            wpend.delete(m.id);
            if (m.type === 'error') p.reject(new Error(m.message)); else p.resolve(m);
        };
        W.onerror = function (e) {
            var err = new Error('op-worker: ' + ((e && e.message) || 'died'));
            wpend.forEach(function (p) { p.reject(err); });
            wpend.clear(); W = null;
        };
        return wcall({ type: 'init', urls: CFG.opUrls });
    }

    /* ── the photo ───────────────────────────────────────────────────────── */
    var demoNext = null;          // the next picked file is an example photo (free)
    async function loadFile(file, detail) {
        if (!file) return;
        var demo = !!(detail && detail.source === 'example') || (demoNext && demoNext === file.name);
        demoNext = null;
        var t0 = performance.now();
        setBusy(T.opening || '…');
        try {
            if (root.HeicDecode && root.HeicDecode.isHeic(file)) file = await root.HeicDecode.normalize(file);
            var bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
            var fw = bmp.width, fh = bmp.height;
            var k = Math.min(1, WORK_LONG / Math.max(fw, fh));
            var w = Math.max(2, Math.round(fw * k)), h = Math.max(2, Math.round(fh * k));
            var c = document.createElement('canvas'); c.width = w; c.height = h;
            var cx = c.getContext('2d', { willReadFrequently: true });
            cx.imageSmoothingQuality = 'high';
            cx.drawImage(bmp, 0, 0, w, h);
            var rgba = cx.getImageData(0, 0, w, h).data;
            // Black and white is a verdict on the PICTURE (ColorizeEngine, the
            // shared one): a white car in a colour photo has no chroma either.
            // The video verdict counts colours OFF the dominant hue, and a warm
            // room — black sofa, white walls, red letters — has one hue: it read
            // as B&W. A photo is B&W only when the tint model leaves (almost)
            // no colour unexplained too (`own`): 0 on every frame of a 1943
            // newsreel, 0.0007–0.19 on colour photos the old rule called grey
            // (measured 22.10.2026, 133 colour + 12 film frames).
            var grey = false;
            try {
                var CE = root.ColorizeEngine;
                if (CE && CE.frameColour && CE.colourVerdict) {
                    var fc = CE.frameColour(c), v = CE.colourVerdict([fc], 1);
                    grey = v.decided && !v.colour && !!fc && fc.own < OWN_GREY;
                }
            } catch (_) { }
            disposeSession();
            S = {
                file: file, name: file.name || 'photo', bitmap: bmp, fw: fw, fh: fh,
                w: w, h: h, rgba: new Uint8Array(rgba.buffer.slice(0)), grey: grey, work: c,
                objs: [], act: -1, nextId: 1, tool: 'pick', brush: 22,
                undo: [], redo: [], ref: null, variants: [], segReady: null,
                out: new Uint8ClampedArray(w * h * 4), showOriginal: false, fileKey: fileKeyOf(file),
                demo: demo,
            };
            S.out.set(S.rgba);
            await ensureWorker();
            await wcall({ type: 'image', buf: S.rgba.slice().buffer, w: w, h: h, imageGrey: grey });
            setupStage();
            show($('prWork'), true);
            $('prKeepFilm').checked = grey;
            show($('prGreyNote'), grey);
            renderObjects();
            repaint();
            setHint(T.stage_hint_first);
            S.facesP = detectFaces(S);
            stage('file_loaded');
            feat('photo_loaded', { mp: +(fw * fh / 1e6).toFixed(1), grey: grey ? 1 : 0, ms: Math.round(performance.now() - t0) });
            // the segmenter: the model downloads once (~20 MB), then lives in the cache
            segPrepare();
        } catch (e) {
            if (root.__handleError) root.__handleError(e, 'photo_recolor_open', { say: T.err_open });
            else toast(T.err_open, 'error');
        } finally { clearBusy(); }
    }
    function fileKeyOf(f) {
        try { return root.__toolLimit && root.__toolLimit.fileKey ? root.__toolLimit.fileKey(f) : (f.name + ':' + f.size + ':' + f.lastModified); }
        catch (_) { return f.name + ':' + f.size; }
    }
    function disposeSession() {
        if (S && S.bitmap && S.bitmap.close) { try { S.bitmap.close(); } catch (_) { } }
        S = null;
    }

    /* ── the segmenter ───────────────────────────────────────────────────── */
    function segPrepare() {
        if (!S) return Promise.resolve(false);
        if (!SEG && root.EdgeTamClient && CFG.seg) SEG = root.EdgeTamClient.create(Object.assign({ feat: feat }, CFG.seg));
        if (!SEG) { S.segReady = false; return Promise.resolve(false); }
        var me = S;
        setStatus(T.seg_loading);
        var frames = [S.rgba];
        if (S.ref) frames.push(S.ref.rgba);
        return SEG.setFrames(frames, S.w, S.h).then(function (ok) {
            if (me !== S) return false;
            S.segReady = !!ok;
            setStatus(ok ? '' : T.seg_unavailable);
            if (!ok) feat('seg_unavailable', {});
            return !!ok;
        }).catch(function (e) {
            if (me === S) { S.segReady = false; setStatus(T.seg_unavailable); }
            feat('seg_failed', { e: String((e && e.message) || e).slice(0, 80) });
            return false;
        });
    }
    async function segPick(index, points, box, mode, avoid) {
        if (S.segReady === null) await segPrepare();
        if (!S.segReady) return null;
        return SEG.pick({ index: index, points: points, box: box || null,
            segOpts: { mode: mode || 'edge', allCands: true, maxFrac: 0.95, refine: mode === 'best' ? false : undefined, avoid: avoid || null } });
    }
    /* The tap plus «not this» points. Several of them can silence the decoder
       (object score −6 with three on a face, 22.10.2026) — then the tap alone,
       still judged against the boxes to stay out of. */
    async function pickAt(p, negs, avoid) {
        var tap = [{ x: p.x, y: p.y, label: 1 }];
        var r = await segPick(0, tap.concat(negs), null, 'edge', avoid);
        if (r && !r.mask && negs.length && (r.reason === 'no-object' || r.reason === 'nothing-at-tap')) {
            feat('negatives_dropped', { n: negs.length, reason: r.reason });
            r = await segPick(0, tap, null, 'edge', avoid);
        }
        return r;
    }
    // Without the segmenter: grow a region of similar colour from the tap.
    function floodSelect(x, y) {
        var w = S.w, h = S.h, d = S.rgba, m = new Uint8Array(w * h), C = root.OpColor;
        var seed = C.rgbToOklab(d[(y * w + x) * 4], d[(y * w + x) * 4 + 1], d[(y * w + x) * 4 + 2]);
        var stack = [y * w + x], tol = 0.075, n = 0;
        m[y * w + x] = 1;
        while (stack.length && n < w * h * 0.9) {
            var p = stack.pop(); n++;
            var px = p % w, py = (p - px) / w;
            var nb = [px > 0 ? p - 1 : -1, px < w - 1 ? p + 1 : -1, py > 0 ? p - w : -1, py < h - 1 ? p + w : -1];
            for (var i = 0; i < 4; i++) {
                var q = nb[i];
                if (q < 0 || m[q]) continue;
                var o = C.rgbToOklab(d[q * 4], d[q * 4 + 1], d[q * 4 + 2]);
                // chroma direction matters more than lightness: shading stays in
                if (Math.hypot((o[0] - seed[0]) * 0.35, o[1] - seed[1], o[2] - seed[2]) < tol) { m[q] = 1; stack.push(q); }
            }
        }
        return m;
    }

    /* ── objects ─────────────────────────────────────────────────────────── */
    function newObject(mask, tap, pick) {
        var o = { id: S.nextId++, mask: mask, removed: null, points: tap ? [{ x: tap.x, y: tap.y, label: 1 }] : [], tap: tap,
                  cands: [], candIdx: -1, an: null, Wc: null, colour: null, keep: null, strength: 1, visible: true };
        if (pick) setCands(o, pick);
        return o;
    }
    // EdgeTAM's answers as «smaller ↔ larger», distinct ones only.
    function setCands(o, pick) {
        var list = (pick.cands || []).filter(function (c) { return c && c.mask && c.area > 16; });
        list.sort(function (a, b) { return a.area - b.area; });
        var out = [];
        list.forEach(function (c) {
            for (var i = 0; i < out.length; i++) if (iouOf(out[i].mask, c.mask) > 0.92) return;
            out.push(c);
        });
        o.cands = out;
        o.candIdx = -1;
        for (var j = 0; j < out.length; j++) if (iouOf(out[j].mask, o.mask) > 0.97) o.candIdx = j;
    }
    function iouOf(a, b) {
        var i = 0, u = 0;
        for (var k = 0; k < a.length; k += 2) { var x = a[k] ? 1 : 0, y = b[k] ? 1 : 0; i += x & y; u += x | y; }
        return u ? i / u : 0;
    }
    function active() { return S && S.act >= 0 ? S.objs[S.act] : null; }
    function objectAt(x, y) {
        var i = y * S.w + x;
        for (var k = S.objs.length - 1; k >= 0; k--) if (S.objs[k].visible && S.objs[k].mask[i]) return k;
        return -1;
    }
    /* EdgeTAM's wider answers sometimes carry specks of scenery — a few
       pixels of cloud above a tank, a puff of dust (21.10.2026, «larger» on a
       B&W M3). A recolour paints every pixel it is given, so islands under 3 %
       of the largest part (and under 0.05 % of the picture) go, unless the
       person put a «this» point inside one.
       And holes under 0.01 % of the picture are filled: where the decoder is
       unsure its upsampling leaves a lattice of pinholes (a sunlit greatcoat
       collar came out dotted olive, 117 holes, 113 of them ≤ 64 px at 1.1 Mpx,
       22.10.2026) — SAM's own mask generator removes holes and islands under
       100 px of its 1024² frame for the same reason. A button is larger. */
    function components(mask, on) {
        var w = S.w, h = S.h, n = w * h, lab = new Int32Array(n), sizes = [0], edge = [0], stack = [], k = 0;
        for (var i = 0; i < n; i++) {
            if ((mask[i] ? 1 : 0) !== on || lab[i]) continue;
            k++; var sz = 0, touches = 0; stack.push(i); lab[i] = k;
            while (stack.length) {
                var p = stack.pop(); sz++;
                var x = p % w, y = (p - x) / w;
                if (x === 0 || y === 0 || x === w - 1 || y === h - 1) touches = 1;
                if (x > 0 && (mask[p - 1] ? 1 : 0) === on && !lab[p - 1]) { lab[p - 1] = k; stack.push(p - 1); }
                if (x < w - 1 && (mask[p + 1] ? 1 : 0) === on && !lab[p + 1]) { lab[p + 1] = k; stack.push(p + 1); }
                if (y > 0 && (mask[p - w] ? 1 : 0) === on && !lab[p - w]) { lab[p - w] = k; stack.push(p - w); }
                if (y < h - 1 && (mask[p + w] ? 1 : 0) === on && !lab[p + w]) { lab[p + w] = k; stack.push(p + w); }
            }
            sizes.push(sz); edge.push(touches);
        }
        return { lab: lab, sizes: sizes, edge: edge, k: k };
    }
    var HOLE_MAX = 0.0001, OWN_GREY = 0.001;   // ≤ 4 px of the 64-px copy: dust on a scan stays B&W
    function cleanMask(mask, points) {
        var w = S.w, n = w * S.h, out = new Uint8Array(n);
        var on = components(mask, 1), big = 0;
        for (var c = 1; c <= on.k; c++) if (on.sizes[c] > big) big = on.sizes[c];
        var keep = new Uint8Array(on.k + 1), floor = on.k < 2 ? 0 : Math.min(0.03 * big, 0.0005 * n);
        for (var c2 = 1; c2 <= on.k; c2++) keep[c2] = on.sizes[c2] >= floor ? 1 : 0;
        (points || []).forEach(function (pt) { if (pt.label) { var l = on.lab[pt.y * w + pt.x]; if (l) keep[l] = 1; } });
        for (var j = 0; j < n; j++) if (on.lab[j] && keep[on.lab[j]]) out[j] = 1;
        var off = components(out, 0), most = Math.max(16, HOLE_MAX * n);
        for (var q = 0; q < n; q++) { var l2 = off.lab[q]; if (l2 && !off.edge[l2] && off.sizes[l2] <= most) out[q] = 1; }
        return out;
    }
    /* Faces stay out of a recoloured garment. A khaki greatcoat and the tanned
       face above it are one hue family, and EdgeTAM's largest answer to a tap
       on the coat was the whole person — the face went burgundy (21.10.2026,
       LoC Kodachrome). Colour rules inside the face box could not tell the
       collar from the cheek (an ellipse cut left an olive notch of collar, a
       skin-hue test took the collar, a grown region left burgundy patches on
       the forehead). So the model is asked the right question instead: the
       middle of every face the tap is not on goes in as a «not this» point,
       and a head that covers it is not an answer (EdgeTamCore.pickByEdge).
       Faces are found once per photo in the background (the shared face-api
       detector /photo-crop uses); a pick waits for them at most 0.8 s. */
    function detectFaces(sess) {
        if (!CFG.facesUrl) return Promise.resolve([]);
        return import(CFG.facesUrl).then(function (m) {
            return m.detect(sess.work, sess.w, sess.h);
        }).then(function (boxes) {
            boxes = boxes || [];
            sess.faces = boxes;
            if (boxes.length) feat('faces_found', { n: boxes.length });
            return boxes;
        }).catch(function () { sess.faces = []; return []; });
    }
    function facesReady(ms) {
        if (!S.facesP) return Promise.resolve([]);
        return Promise.race([S.facesP, new Promise(function (r) { setTimeout(function () { r(S.faces || []); }, ms); })]);
    }
    // The middle of every face the tap is not on — a «not this» point for EdgeTAM…
    function faceNegatives(faces, tap) {
        if (!faces || !faces.length || faceAt(faces, tap.x, tap.y)) return [];
        return faces.map(function (f) { return { x: Math.round(f.x + f.w / 2), y: Math.round(f.y + f.h * 0.5), label: 0 }; });
    }
    /* …and the face's middle band as a box no answer may cover (more than a
       tenth of it): one point at the nose let «Add» on the coat's shaded lapel
       bring back the forehead (22.10.2026). The detector's box is loose
       sideways — it took in the collar beside the cheek — so the band is its
       middle 40 % across and 80 % down. */
    function faceAvoid(faces, tap) {
        if (!faces || !faces.length || faceAt(faces, tap.x, tap.y)) return [];
        return faces.map(function (f) { return { x: f.x + f.w * 0.3, y: f.y + f.h * 0.1, w: f.w * 0.4, h: f.h * 0.8 }; });
    }
    function faceAt(faces, x, y) {
        for (var i = 0; i < faces.length; i++) {
            var f = faces[i];
            if (x >= f.x && y >= f.y && x <= f.x + f.w && y <= f.y + f.h) return true;
        }
        return false;
    }
    function bboxOf(mask) {
        var w = S.w, h = S.h, x0 = w, y0 = h, x1 = -1, y1 = -1;
        for (var y = 0; y < h; y++) for (var x = 0; x < w; x++) if (mask[y * w + x]) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
        return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
    }

    // (Re)analyse an object after its mask changed: analysis + weights in the worker.
    var anSeq = 0;
    async function analyse(o) {
        var my = ++anSeq;
        o.anPending = my;
        setBusy(T.analysing);
        try {
            var r = await wcall({ type: 'analyze', key: o.id, mask: o.mask.slice().buffer, tap: o.tap || null });
            if (o.anPending !== my) return;
            if (!r.an) { o.an = null; o.Wc = null; return; }
            o.an = r.an;
            if (o.keep == null) o.keep = !!r.an.defaultKeep;
            await refreshWeights(o);
            feat('object_analysed', { ms: r.ms || 0, groups: r.an.groups, neutral: r.an.neutral ? 1 : 0, keep: o.keep ? 1 : 0, gloss: r.an.glossJoin || 0 });
        } catch (e) {
            if (root.__handleError) root.__handleError(e, 'photo_recolor_analyse', { say: T.err_generic });
        } finally {
            if (o.anPending === my) clearBusy();
        }
    }
    async function refreshWeights(o) {
        if (!o.an) return;
        var r = await wcall({ type: 'weights', key: o.id, keepPattern: !!o.keep });
        o.Wc = r.Wc;          // W, IN and the edge planes — the export reads the worker's own copy
        paintPreview();
        renderPanel();
    }

    /* ── colour → painter params ─────────────────────────────────────────── */
    function paramsOf(o) {
        if (!o.an || !o.colour) return null;
        var c = o.colour, film = $('prKeepFilm').checked;
        // «keep the photo's lightness»: on a colour photo the tint mode, on a
        // black-and-white one the default; off on B&W = the swatch's brightness.
        var mode = 'auto';
        if (S.grey) mode = film ? (c.lut ? 'lut' : 'tint') : 'relight';
        else if (film) mode = 'tint';
        // the photo's lightness cannot show it (black stays black): the colour's own
        o.lightFromColour = mode === 'tint' && !root.OpObject.tintCarries(o.an, c.rgb);
        if (o.lightFromColour) mode = 'relight';
        return root.OpObject.prepare(o.an, { rgb: c.rgb, lut: c.lut || null, yMed: c.yMed, bright: c.bright || 0, mode: mode }, !!o.keep);
    }
    function layers() {
        var out = [];
        S.objs.forEach(function (o) {
            if (!o.visible || !o.Wc || !o.colour) return;
            var P = paramsOf(o);
            if (P) out.push({ an: o.an, P: P, keepPattern: !!o.keep, strength: o.strength, Wc: o.Wc, key: o.id });
        });
        return out;
    }

    /* ── preview ─────────────────────────────────────────────────────────── */
    var paintQueued = false;
    function paintPreview() {
        if (paintQueued) return;
        paintQueued = true;
        requestAnimationFrame(function () { paintQueued = false; repaint(); });
    }
    function repaint() {
        if (!S) return;
        var cv = $('prCanvas'), cx = cv.getContext('2d');
        if (S.showOriginal) { cx.putImageData(new ImageData(new Uint8ClampedArray(S.rgba), S.w, S.h), 0, 0); drawOverlay(); return; }
        var L = layers();
        if (L.length) root.OpObject.paint(S.rgba, S.out, S.w, S.h, L);
        else S.out.set(S.rgba);
        cx.putImageData(new ImageData(S.out, S.w, S.h), 0, 0);
        drawOverlay();
        updateWarnings();
    }
    // The active object's outline (and a faint one for the others).
    function drawOverlay() {
        var ov = $('prOverlay'), cx = ov.getContext('2d');
        cx.clearRect(0, 0, ov.width, ov.height);
        if (!S || S.showOriginal) return;
        var img = cx.createImageData(S.w, S.h), d = img.data, w = S.w, h = S.h;
        S.objs.forEach(function (o, k) {
            if (!o.visible) return;
            var act = k === S.act, m = o.mask;
            for (var y = 1; y < h - 1; y++) for (var x = 1; x < w - 1; x++) {
                var i = y * w + x;
                if (!m[i] || (m[i - 1] && m[i + 1] && m[i - w] && m[i + w])) continue;
                var p = i * 4, dash = ((x + y) >> 2) & 1;
                if (act) { d[p] = dash ? 255 : 30; d[p + 1] = dash ? 255 : 30; d[p + 2] = dash ? 255 : 30; d[p + 3] = 255; }
                else { d[p] = 255; d[p + 1] = 255; d[p + 2] = 255; d[p + 3] = 110; }
            }
        });
        cx.putImageData(img, 0, 0);
        S.ovImg = img;
    }
    // The box being drawn, over the outlines as they were (no mask walk per move).
    function drawBand(a, b) {
        var ov = $('prOverlay'), cx = ov.getContext('2d');
        if (S.ovImg) cx.putImageData(S.ovImg, 0, 0); else cx.clearRect(0, 0, ov.width, ov.height);
        var x = Math.min(a.x, b.x), y = Math.min(a.y, b.y), w = Math.abs(a.x - b.x), h = Math.abs(a.y - b.y);
        var lw = Math.max(2, Math.round(S.w / 450));
        cx.lineWidth = lw; cx.setLineDash([lw * 4, lw * 3]);
        cx.strokeStyle = 'rgba(0,0,0,.85)'; cx.strokeRect(x, y, w, h);
        cx.lineDashOffset = lw * 3.5; cx.strokeStyle = '#fff'; cx.strokeRect(x, y, w, h);
        cx.setLineDash([]); cx.lineDashOffset = 0;
    }
    function setupStage() {
        var cv = $('prCanvas'), ov = $('prOverlay');
        cv.width = ov.width = S.w; cv.height = ov.height = S.h;
        var st = $('prStage');
        st.style.aspectRatio = S.w + ' / ' + S.h;
        // the width follows the height cap, or a portrait photo is squashed
        st.style.setProperty('--pr-ar', (S.w / S.h).toFixed(4));
    }

    /* ── pointer on the photo ────────────────────────────────────────────── */
    function toWork(ev) {
        var r = $('prOverlay').getBoundingClientRect();
        var x = Math.round((ev.clientX - r.left) / r.width * S.w), y = Math.round((ev.clientY - r.top) / r.height * S.h);
        return { x: Math.max(0, Math.min(S.w - 1, x)), y: Math.max(0, Math.min(S.h - 1, y)) };
    }
    /* «Pick» takes a tap (the browser's click, jitter already allowed for) or
       a BOX drawn by dragging: a black sofa by a white wall came back from
       EdgeTAM as one tufted square or as sofa + wall for any tap (22.10.2026),
       while a box is the person's own statement of extent. The drag starts
       past BOX_START px; the click of the same press is then swallowed. */
    var brushing = null, dragging = null, swallowClick = false, BOX_START = 8;
    function onDown(ev) {
        swallowClick = false;
        if (!S || S.busyLock) return;
        var p = toWork(ev);
        if (S.tool === 'brushAdd' || S.tool === 'brushSub') {
            var o = active(); if (!o) return;
            ev.preventDefault();
            snapshot();
            brushing = { o: o, add: S.tool === 'brushAdd' };
            stamp(o, p, brushing.add);
            try { $('prOverlay').setPointerCapture(ev.pointerId); } catch (_) { }
            return;
        }
        if (S.tool === 'pick' && ev.isPrimary !== false && !(ev.button > 0)) dragging = { id: ev.pointerId, cx: ev.clientX, cy: ev.clientY, p0: p, p1: p, on: false };
    }
    function onMove(ev) {
        if (brushing) { stamp(brushing.o, toWork(ev), brushing.add); return; }
        if (!dragging || ev.pointerId !== dragging.id) return;
        if (!dragging.on) {
            if (Math.hypot(ev.clientX - dragging.cx, ev.clientY - dragging.cy) < BOX_START) return;
            dragging.on = true;
            try { $('prOverlay').setPointerCapture(ev.pointerId); } catch (_) { }
        }
        dragging.p1 = toWork(ev);
        drawBand(dragging.p0, dragging.p1);
    }
    function onUp(ev) {
        if (brushing) { var o = brushing.o; brushing = null; analyse(o); return; }
        var d = dragging; dragging = null;
        if (!d || !d.on || (ev && ev.pointerId !== d.id)) return;      // a tap: the click selects
        swallowClick = true;
        drawOverlay();
        var x0 = Math.min(d.p0.x, d.p1.x), y0 = Math.min(d.p0.y, d.p1.y);
        var box = { x: x0, y: y0, w: Math.abs(d.p1.x - d.p0.x), h: Math.abs(d.p1.y - d.p0.y) };
        if (box.w >= 6 && box.h >= 6) pickObject(null, box);
    }
    function onCancel() {
        if (brushing) { var o = brushing.o; brushing = null; analyse(o); return; }
        if (dragging) { dragging = null; drawOverlay(); }
    }
    function stamp(o, p, add) {
        var r = Math.max(2, Math.round(S.brush * S.w / Math.max(1, $('prOverlay').getBoundingClientRect().width))), w = S.w, h = S.h;
        for (var y = Math.max(0, p.y - r); y <= Math.min(h - 1, p.y + r); y++)
            for (var x = Math.max(0, p.x - r); x <= Math.min(w - 1, p.x + r); x++)
                if ((x - p.x) * (x - p.x) + (y - p.y) * (y - p.y) <= r * r) {
                    o.mask[y * w + x] = add ? 1 : 0;
                    if (!add) (o.removed || (o.removed = new Uint8Array(w * h)))[y * w + x] = 1;
                    else if (o.removed) o.removed[y * w + x] = 0;
                }
        drawOverlay();
    }
    // A click (a tap the browser already de-jittered): select, refine, sample.
    async function onClick(ev) {
        if (swallowClick && ev.detail !== 0) { swallowClick = false; return; }
        if (!S || S.busyLock) return;
        var p = toWork(ev), o = active();
        if (S.tool === 'eyedrop') { setColour(sampleColour(p), 'eyedrop'); setTool('pick'); return; }
        if (S.tool === 'brushAdd' || S.tool === 'brushSub') return;
        /* «Add» / «Remove»: the part under THIS tap is segmented on its own and
           joined to (or cut from) the object — the way selection tools work.
           Re-asking EdgeTAM with every point plus the object's box answered a
           yellow raincoat's left panel in 8-px blocks and dropped its hood
           (21.10.2026): with several points the decoder answers SOME part. */
        if ((S.tool === 'add' || S.tool === 'sub') && o) {
            var add = S.tool === 'add';
            lock(true);
            try {
                /* «Remove» asks for the thing under the tap that is NOT the
                   object's own taps: alone, a tap on sandbags beside a khaki
                   coat answered sandbags + coat, and cutting that took the coat
                   away too. «Add» keeps out what was removed before and faces. */
                var negs = [], faces = add ? await facesReady(800) : [];
                o.points.forEach(function (pt) { if (add ? !pt.label : pt.label) negs.push({ x: pt.x, y: pt.y, label: 0 }); });
                if (!add && !negs.length) { var ip = insidePoint(o); if (ip) negs.push({ x: ip.x, y: ip.y, label: 0 }); }
                var r = await pickAt(p, negs.concat(faceNegatives(faces, p)), faceAvoid(faces, p));
                var part = r && r.mask ? cleanMask(r.mask, [{ x: p.x, y: p.y, label: 1 }]) : null;
                if (!part && !r) part = floodSelect(p.x, p.y);     // no segmenter here
                if (!part) { toast(T.nothing_here, 'info'); return; }
                /* What was removed stays removed: «Add» on the coat's shaded
                   lapel came back with the sandbags taken out a step before
                   (22.10.2026). «Add» ON a removed place means «bring it back». */
                var m = o.mask.slice(), rm = o.removed ? o.removed.slice() : (add ? null : new Uint8Array(m.length)), n = 0;
                var back = add && rm && rm[p.y * S.w + p.x];
                for (var i = 0; i < m.length; i++) {
                    if (part[i]) {
                        if (!add) { m[i] = 0; rm[i] = 1; }
                        else if (back || !rm || !rm[i]) { m[i] = 1; if (rm) rm[i] = 0; }
                    }
                    n += m[i];
                }
                if (!n) { toast(T.nothing_here, 'info'); return; }
                snapshot();
                o.points.push({ x: p.x, y: p.y, label: add ? 1 : 0 });
                o.mask = m; o.removed = rm;
                o.cands = []; o.candIdx = -1;        // «smaller / larger» belong to a single answer
                drawOverlay();
                await analyse(o);
                feat('mask_edit', { op: add ? 'add' : 'sub', partFrac: +(countOf(part) / (S.w * S.h)).toFixed(3) });
            } finally { lock(false); }
            return;
        }
        // pick: an existing object under the tap becomes the active one
        var hit = objectAt(p.x, p.y);
        if (hit >= 0) { S.act = hit; renderObjects(); renderPanel(); drawOverlay(); return; }
        await pickObject(p, null);
    }
    // A new object from a tap `p` or a drawn `box` (frame pixels).
    async function pickObject(p, box) {
        if (S.objs.length >= MAX_OBJECTS) { toast(fmt(T.too_many_objects, { n: MAX_OBJECTS }), 'warning'); return; }
        lock(true);
        setHint(T.selecting);
        try {
            var t0 = performance.now();
            var faces = await facesReady(800), pick;
            // a box is the extent the person drew: the head that fills it best answers
            if (box) pick = await segPick(0, [], box, 'box');
            else pick = await pickAt(p, faceNegatives(faces, p), faceAvoid(faces, p));
            var mask = pick && pick.mask;
            if (!mask) {
                if (pick || box) {
                    feat('nothing_at_tap', { reason: (pick && pick.reason) || 'no-seg', obj: pick && pick.obj, negs: faces.length, box: box ? 1 : 0,
                        c: ((pick && pick.cands) || []).map(function (c) { return [+(c.area / (S.w * S.h)).toFixed(3), c.iou]; }) });
                    toast(T.nothing_here, 'info'); setHint(T.stage_hint_first); return;
                }
                mask = floodSelect(p.x, p.y);                 // no segmenter on this device
                feat('flood_select', {});
            }
            snapshot();
            if (pick) mask = cleanMask(mask, p ? [{ x: p.x, y: p.y, label: 1 }] : []);
            var obj = newObject(mask, p, pick);
            S.objs.push(obj); S.act = S.objs.length - 1;
            renderObjects();
            drawOverlay();
            await analyse(obj);
            setHint(obj.colour ? '' : T.stage_hint_colour);
            feat('object_picked', { ms: Math.round(performance.now() - t0), via: box ? 'box' : (pick ? 'seg' : 'flood'), n: S.objs.length, frac: +(countOf(mask) / (S.w * S.h)).toFixed(3) });
            stage('object_picked');
        } catch (e) {
            if (root.__handleError) root.__handleError(e, 'photo_recolor_pick', { say: T.err_generic });
        } finally { lock(false); }
    }
    // A point surely inside the object (a box selection has no tap): the box
    // middle or the centroid, when the mask is there.
    function insidePoint(o) {
        if (o.tap) return o.tap;
        var b = bboxOf(o.mask); if (!b) return null;
        var c = { x: Math.round(b.x + b.w / 2), y: Math.round(b.y + b.h / 2) };
        if (o.mask[c.y * S.w + c.x]) return c;
        var sx = 0, sy = 0, n = 0;
        for (var y = b.y; y < b.y + b.h; y += 2) for (var x = b.x; x < b.x + b.w; x += 2) if (o.mask[y * S.w + x]) { sx += x; sy += y; n++; }
        if (!n) return null;
        c = { x: Math.round(sx / n), y: Math.round(sy / n) };
        return o.mask[c.y * S.w + c.x] ? c : null;
    }
    function countOf(m) { var n = 0; for (var i = 0; i < m.length; i++) n += m[i] ? 1 : 0; return n; }
    function sampleColour(p) {
        var rs = [], gs = [], bs = [], d = S.rgba;
        for (var y = p.y - 2; y <= p.y + 2; y++) for (var x = p.x - 2; x <= p.x + 2; x++) {
            if (x < 0 || y < 0 || x >= S.w || y >= S.h) continue;
            var i = (y * S.w + x) * 4; rs.push(d[i]); gs.push(d[i + 1]); bs.push(d[i + 2]);
        }
        function med(a) { a.sort(function (u, v) { return u - v; }); return a[a.length >> 1]; }
        return [med(rs), med(gs), med(bs)];
    }

    /* ── smaller / larger ────────────────────────────────────────────────── */
    /* «Larger» past EdgeTAM's own answers: a tap on one tufted square of a
       black sofa came back as that square or two (0.017 / 0.019 of the frame)
       and the whole sofa was never among them (22.10.2026). The selection's box,
       grown by half (then by 1.5) each way, is asked again with the same tap; an
       answer that holds the selection and is clearly larger is the next step. */
    async function growCand(o) {
        var b = bboxOf(o.mask); if (!b) return null;
        var tap = insidePoint(o); if (!tap) return null;
        var cur = countOf(o.mask), faces = S.faces || [];
        for (var gi = 0; gi < GROW.length; gi++) {
            var g = GROW[gi], x0 = Math.max(0, b.x - b.w * g), y0 = Math.max(0, b.y - b.h * g);
            var box = { x: x0, y: y0, w: Math.min(S.w, b.x + b.w * (1 + g)) - x0, h: Math.min(S.h, b.y + b.h * (1 + g)) - y0 };
            var r = await segPick(0, [{ x: tap.x, y: tap.y, label: 1 }].concat(faceNegatives(faces, tap)), box, 'edge', faceAvoid(faces, tap));
            if (!r) continue;
            /* the NEXT size up, not the largest: the largest answer for a sofa
               at 1.5× was the sofa plus the wall behind it (0.66 of the frame) */
            var pool = (r.cands || []).filter(function (c) { return c && c.mask; }).map(function (c) { return c.mask; });
            if (r.mask) pool.push(r.mask);
            var best = null, bestN = Infinity;
            pool.forEach(function (pm) {
                var m = cleanMask(pm, [{ x: tap.x, y: tap.y, label: 1 }]), n = countOf(m);
                if (n < cur * 1.15 || n >= bestN || holdsOf(m, o.mask) < 0.85) return;
                best = m; bestN = n;
            });
            if (!best) continue;
            feat('grow', { g: g, from: +(cur / (S.w * S.h)).toFixed(3), to: +(bestN / (S.w * S.h)).toFixed(3) });
            return { mask: best, area: bestN };
        }
        return null;
    }
    var GROW = [0.5, 1.5];
    function holdsOf(big, small) {
        var inS = 0, both = 0;
        for (var i = 0; i < small.length; i += 2) if (small[i]) { inS++; if (big[i]) both++; }
        return inS ? both / inS : 0;
    }
    async function stepCand(dir) {
        var o = active(); if (!o || S.busyLock) return;
        // a selection edited by hand (or cleaned) is not one of the answers: step by area
        var cur = countOf(o.mask), idx = -1;
        if (o.candIdx >= 0) idx = o.candIdx + dir;
        else if (dir > 0) { idx = o.cands.length; for (var i = 0; i < o.cands.length; i++) if (o.cands[i].area > cur * 1.05) { idx = i; break; } }
        else for (var j = o.cands.length - 1; j >= 0; j--) if (o.cands[j].area < cur * 0.95) { idx = j; break; }
        if (dir > 0 && idx >= o.cands.length) {
            lock(true); setBusy(T.selecting);
            var grown = null;
            try { grown = await growCand(o); } finally { lock(false); clearBusy(); }
            if (grown) { o.cands.push(grown); idx = o.cands.length - 1; }
        }
        if (idx < 0 || idx >= o.cands.length) { toast(dir > 0 ? T.no_larger : T.no_smaller, 'info'); return; }
        snapshot();
        o.candIdx = idx; o.mask = cleanMask(o.cands[idx].mask, o.points);
        drawOverlay();
        await analyse(o);
        feat('cand_step', { dir: dir, idx: idx, of: o.cands.length });
    }

    /* ── colour ──────────────────────────────────────────────────────────── */
    function setColour(rgb, src, extra) {
        var o = active();
        if (!o) { toast(T.pick_first, 'info'); return; }
        snapshot();
        o.colour = Object.assign({ rgb: rgb, src: src }, extra || {});
        $('prHex').value = root.OpColor.rgbToHex(rgb);
        $('prColor').value = root.OpColor.rgbToHex(rgb);
        setHint('');
        paintPreview();
        renderObjects();
        renderPanel();
        feat('colour_set', { src: src, grey: S.grey ? 1 : 0, lut: extra && extra.lut ? 1 : 0 });
    }
    function buildSwatches() {
        var box = $('prSwatches'); if (!box) return;
        box.innerHTML = '';
        (CFG.palette || []).forEach(function (hex) {
            var b = document.createElement('button');
            b.type = 'button'; b.className = 'pr-sw'; b.style.background = hex; b.title = hex; b.setAttribute('aria-label', hex);
            b.dataset.hex = hex;
            on(b, 'click', function () { setColour(root.OpColor.hexToRgb(hex), 'swatch'); });
            box.appendChild(b);
        });
    }

    /* ── the reference photo ─────────────────────────────────────────────── */
    async function loadReference(file) {
        if (!S || !file) return;
        setBusy(T.opening);
        try {
            if (root.HeicDecode && root.HeicDecode.isHeic(file)) file = await root.HeicDecode.normalize(file);
            var bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
            // letterboxed into the working size: the segmenter's frames share one size
            var k = Math.min(S.w / bmp.width, S.h / bmp.height);
            var rw = Math.max(2, Math.round(bmp.width * k)), rh = Math.max(2, Math.round(bmp.height * k));
            var ox = Math.floor((S.w - rw) / 2), oy = Math.floor((S.h - rh) / 2);
            var c = document.createElement('canvas'); c.width = S.w; c.height = S.h;
            var cx = c.getContext('2d', { willReadFrequently: true });
            cx.fillStyle = '#000'; cx.fillRect(0, 0, S.w, S.h);
            cx.drawImage(bmp, ox, oy, rw, rh);
            try { bmp.close && bmp.close(); } catch (_) { }
            S.ref = { canvas: c, rgba: new Uint8Array(cx.getImageData(0, 0, S.w, S.h).data.buffer.slice(0)), box: { x: ox, y: oy, w: rw, h: rh }, points: [], mask: null };
            var rc = $('prRefCanvas'); rc.width = S.w; rc.height = S.h;
            rc.getContext('2d').drawImage(c, 0, 0);
            show($('prRefBox'), true);
            setRefHint(T.ref_hint);
            S.segReady = null;               // the frame set changed: [photo, reference]
            segPrepare();
            feat('ref_loaded', {});
        } catch (e) {
            if (root.__handleError) root.__handleError(e, 'photo_recolor_ref', { say: T.err_open });
        } finally { clearBusy(); }
    }
    async function onRefClick(ev) {
        if (!S || !S.ref || S.busyLock) return;
        if (!active()) { toast(T.pick_first, 'info'); return; }
        var r = $('prRefCanvas').getBoundingClientRect();
        var p = { x: Math.round((ev.clientX - r.left) / r.width * S.w), y: Math.round((ev.clientY - r.top) / r.height * S.h) };
        var b = S.ref.box;
        if (p.x < b.x || p.y < b.y || p.x >= b.x + b.w || p.y >= b.y + b.h) return;
        var label = S.refTool === 'sub' ? 0 : 1;
        S.ref.points = (S.refTool === 'add' || S.refTool === 'sub') && S.ref.mask ? S.ref.points.concat([{ x: p.x, y: p.y, label: label }]) : [{ x: p.x, y: p.y, label: 1 }];
        lock(true); setRefHint(T.selecting);
        try {
            var pick = await segPick(1, S.ref.points, S.ref.points.length > 1 && S.ref.mask ? bboxOf(S.ref.mask) : null, S.ref.points.length > 1 ? 'best' : 'edge');
            var mask = pick && pick.mask;
            if (!mask) {
                if (pick) { setRefHint(T.nothing_here); return; }
                // no segmenter: a small disc around the tap is still a fair sample
                mask = new Uint8Array(S.w * S.h);
                for (var y = p.y - 12; y <= p.y + 12; y++) for (var x = p.x - 12; x <= p.x + 12; x++)
                    if (x >= 0 && y >= 0 && x < S.w && y < S.h && (x - p.x) * (x - p.x) + (y - p.y) * (y - p.y) <= 144) mask[y * S.w + x] = 1;
            }
            S.ref.mask = mask;
            drawRefOverlay();
            var res = await wcall({ type: 'reference', buf: S.ref.rgba.slice().buffer, w: S.w, h: S.h, mask: mask.slice().buffer });
            if (!res.ref) { setRefHint(T.nothing_here); return; }
            setColour(res.ref.rgb, 'reference', { lut: res.ref.lut, yMed: res.ref.yMed });
            setRefHint(res.ref.small ? T.ref_small : T.ref_done);
            feat('ref_region', { px: res.ref.px || 0, small: res.ref.small ? 1 : 0 });
        } catch (e) {
            if (root.__handleError) root.__handleError(e, 'photo_recolor_ref_pick', { say: T.err_generic });
        } finally { lock(false); }
    }
    function drawRefOverlay() {
        var rc = $('prRefCanvas'), cx = rc.getContext('2d');
        cx.drawImage(S.ref.canvas, 0, 0);
        if (!S.ref.mask) return;
        var img = cx.getImageData(0, 0, S.w, S.h), d = img.data, m = S.ref.mask, w = S.w;
        for (var i = w; i < m.length - w; i++) {
            if (!m[i] || (m[i - 1] && m[i + 1] && m[i - w] && m[i + w])) continue;
            d[i * 4] = 255; d[i * 4 + 1] = 255; d[i * 4 + 2] = 255;
        }
        cx.putImageData(img, 0, 0);
    }

    /* ── undo / redo ─────────────────────────────────────────────────────── */
    function snapshot() {
        if (!S) return;
        S.undo.push(stateOf());
        if (S.undo.length > 40) S.undo.shift();
        S.redo.length = 0;
        renderUndo();
    }
    function stateOf() {
        return { act: S.act, objs: S.objs.map(function (o) { return { id: o.id, mask: o.mask.slice(), removed: o.removed ? o.removed.slice() : null, points: o.points.slice(), tap: o.tap, cands: o.cands, candIdx: o.candIdx,
            colour: o.colour, keep: o.keep, strength: o.strength, visible: o.visible }; }) };
    }
    async function restore(st) {
        var before = {};
        S.objs.forEach(function (o) { before[o.id] = o; });
        S.objs = st.objs.map(function (s) {
            var prev = before[s.id], o = Object.assign({ an: null, Wc: null }, s);
            o.mask = s.mask.slice(); o.removed = s.removed ? s.removed.slice() : null;
            if (prev && prev.an && sameMask(prev.mask, o.mask)) { o.an = prev.an; o.Wc = prev.Wc && prev.keep === o.keep ? prev.Wc : null; }
            return o;
        });
        S.act = Math.min(st.act, S.objs.length - 1);
        renderObjects(); renderPanel(); drawOverlay();
        for (var i = 0; i < S.objs.length; i++) {
            var o = S.objs[i];
            if (!o.an) await analyse(o);
            else if (!o.Wc) await refreshWeights(o);
        }
        paintPreview();
    }
    function sameMask(a, b) { if (a.length !== b.length) return false; for (var i = 0; i < a.length; i += 7) if ((a[i] ? 1 : 0) !== (b[i] ? 1 : 0)) return false; return true; }
    function doUndo() { if (!S || !S.undo.length || S.busyLock) return; S.redo.push(stateOf()); restore(S.undo.pop()); renderUndo(); }
    function doRedo() { if (!S || !S.redo.length || S.busyLock) return; S.undo.push(stateOf()); restore(S.redo.pop()); renderUndo(); }
    function renderUndo() { $('prUndo').disabled = !S || !S.undo.length; $('prRedo').disabled = !S || !S.redo.length; }

    /* ── panels ──────────────────────────────────────────────────────────── */
    function renderObjects() {
        var ul = $('prObjs'); if (!ul || !S) return;
        ul.innerHTML = '';
        S.objs.forEach(function (o, k) {
            var li = document.createElement('li');
            li.className = 'pr-obj' + (k === S.act ? ' on' : '');
            var sw = document.createElement('span'); sw.className = 'pr-obj-sw';
            if (o.colour) sw.style.background = root.OpColor.rgbToHex(o.colour.rgb); else sw.classList.add('none');
            var nm = document.createElement('button'); nm.type = 'button'; nm.className = 'pr-obj-name';
            nm.textContent = fmt(T.object_n, { n: k + 1 });
            on(nm, 'click', function () { S.act = k; renderObjects(); renderPanel(); drawOverlay(); });
            var eye = document.createElement('button'); eye.type = 'button'; eye.className = 'pr-obj-act';
            eye.textContent = o.visible ? '👁' : '◌'; eye.title = o.visible ? T.hide : T.show_obj; eye.setAttribute('aria-label', eye.title);
            on(eye, 'click', function () { snapshot(); o.visible = !o.visible; renderObjects(); paintPreview(); });
            var del = document.createElement('button'); del.type = 'button'; del.className = 'pr-obj-act';
            del.textContent = '✕'; del.title = T.remove_obj; del.setAttribute('aria-label', T.remove_obj);
            on(del, 'click', function () {
                snapshot();
                S.objs.splice(k, 1);
                try { wcall({ type: 'forget', key: o.id }); } catch (_) { }
                S.act = Math.min(S.act, S.objs.length - 1);
                renderObjects(); renderPanel(); paintPreview();
            });
            li.appendChild(sw); li.appendChild(nm); li.appendChild(eye); li.appendChild(del);
            ul.appendChild(li);
        });
        show($('prObjEmpty'), !S.objs.length);
    }
    function renderPanel() {
        var o = active();
        show($('prColorPanel'), !!o);
        show($('prSavePanel'), !!(S && S.objs.some(function (x) { return x.colour; })));
        show($('prVarPanel'), !!o && !!o.colour);
        // «larger» can always ask the segmenter again (growCand); «smaller» needs answers
        var sm = $('prSmaller'), lg = $('prLarger');
        if (sm) sm.disabled = !o || !o.cands.length;
        if (lg) lg.disabled = !o || (!o.cands.length && S.segReady !== true);
        ['prToolAdd', 'prToolSub', 'prBrushAdd', 'prBrushSub'].forEach(function (id) { var b = $(id); if (b) b.disabled = !o; });
        if (!o) return;
        $('prKeep').checked = !!o.keep;
        $('prStrength').value = Math.round(o.strength * 100);
        $('prStrengthVal').textContent = Math.round(o.strength * 100) + '%';
        if (o.colour) { $('prHex').value = root.OpColor.rgbToHex(o.colour.rgb); $('prColor').value = $('prHex').value; }
        Array.prototype.forEach.call(document.querySelectorAll('.pr-sw'), function (b) {
            b.classList.toggle('on', !!(o.colour && b.dataset.hex.toLowerCase() === root.OpColor.rgbToHex(o.colour.rgb)));
        });
        renderVariants();
    }
    function updateWarnings() {
        var o = active(), warn = false, light = false;
        if (o && o.colour && o.an) {
            var P = paramsOf(o);
            light = !!o.lightFromColour;
            // a dark gloss made much lighter reads as metallic, not as paint
            warn = !S.grey && !!(P && P.mode === 'relight' && P.R.rho > 5 && o.an.neutral);
        }
        show($('prWarnDark'), warn);
        show($('prWarnLight'), light);
    }
    function setTool(t) {
        if (!S) return;
        S.tool = t;
        Array.prototype.forEach.call(document.querySelectorAll('[data-tool]'), function (b) {
            var onb = b.dataset.tool === t; b.classList.toggle('on', onb); b.setAttribute('aria-pressed', onb ? 'true' : 'false');
        });
        $('prOverlay').style.cursor = t === 'eyedrop' ? 'copy' : (t.indexOf('brush') === 0 ? 'cell' : 'crosshair');
        show($('prBrushRow'), t === 'brushAdd' || t === 'brushSub');
        setHint(t === 'add' ? T.hint_add : t === 'sub' ? T.hint_sub : t === 'eyedrop' ? T.hint_eyedrop : t.indexOf('brush') === 0 ? T.hint_brush : '');
    }
    function setHint(s) { var el = $('prStageHint'); if (!el) return; el.textContent = s || ''; el.classList.toggle('pr-off', !s); }
    function setRefHint(s) { var el = $('prRefHint'); if (el) el.textContent = s || ''; }
    function setStatus(s) { var el = $('prStatus'); if (!el) return; el.textContent = s || ''; show(el, !!s); }
    var busyN = 0;
    function setBusy(label) { busyN++; var b = $('prBusy'); if (b) { b.textContent = label || ''; show(b, true); } }
    function clearBusy() { busyN = Math.max(0, busyN - 1); if (!busyN) show($('prBusy'), false); }
    function lock(v) { if (S) S.busyLock = v; }

    /* ── variants ────────────────────────────────────────────────────────── */
    function renderVariants() {
        var box = $('prVarList'); if (!box || !S) return;
        box.innerHTML = '';
        S.variants.forEach(function (rgb, i) {
            var b = document.createElement('button'); b.type = 'button'; b.className = 'pr-sw sm';
            b.style.background = root.OpColor.rgbToHex(rgb); b.title = T.var_remove; b.setAttribute('aria-label', T.var_remove + ' ' + root.OpColor.rgbToHex(rgb));
            on(b, 'click', function () { S.variants.splice(i, 1); renderVariants(); });
            box.appendChild(b);
        });
        $('prVarGo').disabled = S.variants.length < 2;
        $('prVarCount').textContent = S.variants.length ? fmt(T.var_count, { n: S.variants.length }) : '';
    }

    /* ── export ──────────────────────────────────────────────────────────── */
    function outType() {
        var v = $('prFormat').value;
        if (v === 'auto') return /png$/i.test(S.name) ? 'image/png' : (/webp$/i.test(S.name) ? 'image/webp' : 'image/jpeg');
        return v;
    }
    function extOf(type) { return type === 'image/png' ? 'png' : type === 'image/webp' ? 'webp' : 'jpg'; }
    function nameFor(rgbs) {
        var stem = String(S.name).replace(/\.[^.]+$/, '') || 'photo';
        var parts = [stem].concat(rgbs.map(function (c) { return root.OpColor.rgbToHex(c).slice(1); }));
        var nm = root.__outputName ? root.__outputName.join(parts, extOf(outType())) : '';
        return nm || (stem + '-recolor.' + extOf(outType()));
    }
    // The full-size photo, painted strip by strip by the worker. → Blob
    async function renderFull(L, onProgress) {
        var fw = S.fw, fh = S.fh, out = document.createElement('canvas');
        out.width = fw; out.height = fh;
        var ocx = out.getContext('2d');
        var strip = document.createElement('canvas'); strip.width = fw;
        var scx = null;
        var wl = L.map(function (l) { return { key: l.key, P: l.P, keepPattern: l.keepPattern, strength: l.strength }; });
        for (var y = 0; y < fh; y += STRIP) {
            var sh = Math.min(STRIP, fh - y);
            if (strip.height !== sh) { strip.height = sh; scx = null; }
            scx = scx || strip.getContext('2d', { willReadFrequently: true });
            scx.clearRect(0, 0, fw, sh);
            scx.drawImage(S.bitmap, 0, y, fw, sh, 0, 0, fw, sh);
            var buf = scx.getImageData(0, 0, fw, sh).data.buffer;
            var r = await wcall({ type: 'paintRows', buf: buf, w: fw, h: fh, y0: y, y1: y + sh, layers: wl }, [buf]);
            ocx.putImageData(new ImageData(new Uint8ClampedArray(r.buf), fw, sh), 0, y);
            if (onProgress) onProgress(Math.min(1, (y + sh) / fh));
        }
        var type = outType(), q = (+$('prQuality').value || 92) / 100;
        return root.__canvasBlob(out, type, q);
    }
    function billRun() {
        // an example photo is the tool showing itself — never billed (as /photo-colorize's demos)
        if (!root.__toolLimit || S.billed || S.demo) return { commit: function () { }, abort: function () { }, free: true };
        return root.__toolLimit.begin(SLUG, S.fileKey);
    }
    async function save() {
        if (!S || S.busyLock) return;
        var L = layers();
        if (!L.length) { toast(T.pick_colour_first, 'info'); return; }
        var run = billRun();
        if (!run) return;                     // the limit note is on screen
        lock(true);
        var btn = $('prSave'), label = btn.textContent, t0 = performance.now();
        btn.disabled = true;
        try {
            var blob = await renderFull(L, function (p) { btn.textContent = fmt(T.saving_pct, { p: Math.round(p * 100) }); });
            var name = nameFor(S.objs.filter(function (o) { return o.visible && o.colour; }).map(function (o) { return o.colour.rgb; }));
            var ok = root.__downloadFile ? await root.__downloadFile(blob, name) : false;
            if (ok !== false) {
                run.commit(); S.billed = true;
                stage('export_done');
                try { root.__aeToolComplete && root.__aeToolComplete({ slug: SLUG, kind: 'photo', objects: L.length, mp: +(S.fw * S.fh / 1e6).toFixed(1), ms: Math.round(performance.now() - t0) }); } catch (_) { }
                show($('prSaved'), true);
            } else run.abort();
        } catch (e) {
            run.abort();
            if (root.__handleError) root.__handleError(e, 'photo_recolor_save', { say: T.err_save });
        } finally {
            btn.textContent = label; btn.disabled = false; lock(false);
        }
    }
    async function saveVariants() {
        if (!S || S.busyLock) return;
        var o = active();
        if (!o || !o.an || S.variants.length < 2) return;
        // the archive door has to be the first await in the click
        var bd = root.__batchDeliver;
        if (!bd) { toast(T.err_save, 'error'); return; }
        var run = billRun();
        if (!run) return;
        lock(true);
        var btn = $('prVarGo'), label = btn.textContent, keep = o.colour, items = [];
        btn.disabled = true;
        try {
            for (var i = 0; i < S.variants.length; i++) {
                o.colour = Object.assign({}, keep || {}, { rgb: S.variants[i], lut: null, src: 'variant' });
                var L = layers();
                var blob = await renderFull(L, function (p) { btn.textContent = fmt(T.var_progress, { i: i + 1, n: S.variants.length, p: Math.round(p * 100) }); });
                items.push({ name: nameFor([S.variants[i]]), blob: blob });
            }
            o.colour = keep;
            var stem = String(S.name).replace(/\.[^.]+$/, '') || 'photo';
            var res = await bd.deliver(items, { slug: SLUG, zipName: stem + '-colours.zip', archiveOnly: true });
            if (res && res.written) {
                run.commit(); S.billed = true;
                feat('variants_saved', { n: items.length });
                try { root.__aeToolComplete && root.__aeToolComplete({ slug: SLUG, kind: 'variants', count: items.length }); } catch (_) { }
            } else run.abort();
        } catch (e) {
            o.colour = keep;
            run.abort();
            if (root.__handleError) root.__handleError(e, 'photo_recolor_variants', { say: T.err_save });
        } finally {
            btn.textContent = label; renderVariants(); lock(false); paintPreview();
        }
    }

    /* ── wiring ──────────────────────────────────────────────────────────── */
    function wire() {
        var zone = $('prUpload');
        var bind = root.__bindUploaderFile;
        if (bind) bind(zone, function (f, detail) { loadFile(f, detail); });
        else on(zone, 'tool-uploader:file', function (e) { loadFile(e.detail && e.detail.file, e.detail); });

        // Examples: the before/after wipes, and «try it on this photo» — the
        // photo goes in through the uploader itself, the same path as a pick.
        Array.prototype.forEach.call(document.querySelectorAll('.pr-ex .cs-wrap'), function (wrap) {
            var after = wrap.querySelector('.pr-ex-after');
            function clip(p) { if (after) after.style.clipPath = 'inset(0 0 0 ' + (p * 100).toFixed(1) + '%)'; }
            if (root.CompareSlider) { try { root.CompareSlider.attach(wrap, { initialPos: 0.5, onPosChange: clip }); } catch (_) { } }
        });
        Array.prototype.forEach.call(document.querySelectorAll('[data-pr-example]'), function (b) {
            on(b, 'click', function () {
                var url = b.dataset.prExample, id = b.dataset.prExampleId || 'example';
                b.disabled = true;
                fetch(url).then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.blob(); }).then(function (blob) {
                    var f = new File([blob], id + '.jpg', { type: blob.type || 'image/jpeg' });
                    demoNext = f.name;
                    if (typeof zone.__uploaderInjectFile === 'function') zone.__uploaderInjectFile(f, 'example');
                    else loadFile(f, { source: 'example' });
                    feat('example_try', { id: id });
                }).catch(function (e) {
                    if (root.__handleError) root.__handleError(e, 'photo_recolor_example', { say: T.err_open });
                }).then(function () { b.disabled = false; });
            });
        });
        on(zone, 'tool-uploader:clear', function () { disposeSession(); show($('prWork'), false); });

        var ov = $('prOverlay');
        on(ov, 'click', onClick);
        on(ov, 'pointerdown', onDown);
        on(ov, 'pointermove', onMove);
        on(ov, 'pointerup', onUp);
        on(ov, 'pointercancel', onCancel);
        on(ov, 'touchmove', function (e) { if (brushing || (dragging && dragging.on)) e.preventDefault(); }, { passive: false });

        Array.prototype.forEach.call(document.querySelectorAll('[data-tool]'), function (b) {
            on(b, 'click', function () { setTool(S && S.tool === b.dataset.tool && b.dataset.tool !== 'pick' ? 'pick' : b.dataset.tool); });
        });
        on($('prBrushSize'), 'input', function () { if (S) S.brush = +this.value; });
        on($('prSmaller'), 'click', function () { stepCand(-1); });
        on($('prLarger'), 'click', function () { stepCand(1); });
        on($('prUndo'), 'click', doUndo);
        on($('prRedo'), 'click', doRedo);
        on(document, 'keydown', function (e) {
            if (!S || (root.__keyOwnedByFocus && root.__keyOwnedByFocus(e))) return;
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'z' || e.key === 'Z' || e.key === 'я' || e.key === 'Я')) { e.preventDefault(); doUndo(); }
            else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || e.key === 'Y' || (e.shiftKey && (e.key === 'z' || e.key === 'Z')))) { e.preventDefault(); doRedo(); }
        });
        // hold to see the original
        var cmp = $('prCompare');
        function cmpOn(e) { if (!S) return; if (e) e.preventDefault(); S.showOriginal = true; repaint(); }
        function cmpOff() { if (!S || !S.showOriginal) return; S.showOriginal = false; repaint(); }
        on(cmp, 'pointerdown', cmpOn); on(cmp, 'pointerup', cmpOff); on(cmp, 'pointerleave', cmpOff); on(cmp, 'pointercancel', cmpOff);
        on(cmp, 'keydown', function (e) { if (e.key === ' ' || e.key === 'Enter') cmpOn(e); });
        on(cmp, 'keyup', function (e) { if (e.key === ' ' || e.key === 'Enter') cmpOff(); });

        on($('prColor'), 'input', function () { var c = root.OpColor.hexToRgb(this.value); if (c) setColour(c, 'picker'); });
        on($('prHex'), 'change', function () {
            var c = root.OpColor.hexToRgb(this.value);
            if (c) setColour(c, 'hex'); else toast(T.err_hex, 'warning');
        });
        on($('prEyedrop'), 'click', function () { setTool(S && S.tool === 'eyedrop' ? 'pick' : 'eyedrop'); });
        on($('prKeep'), 'change', function () { var o = active(); if (!o) return; snapshot(); o.keep = this.checked; refreshWeights(o); feat('keep_toggle', { on: o.keep ? 1 : 0 }); });
        on($('prKeepFilm'), 'change', function () { paintPreview(); renderPanel(); feat('film_toggle', { on: this.checked ? 1 : 0, grey: S && S.grey ? 1 : 0 }); });
        on($('prStrength'), 'input', function () { var o = active(); if (!o) return; o.strength = (+this.value) / 100; $('prStrengthVal').textContent = this.value + '%'; paintPreview(); });
        on($('prStrength'), 'change', function () { snapshot(); });

        on($('prRefBtn'), 'click', function () { $('prRefInput').click(); });
        on($('prRefInput'), 'change', function () { if (this.files && this.files[0]) loadReference(this.files[0]); this.value = ''; });
        on($('prRefCanvas'), 'click', onRefClick);
        Array.prototype.forEach.call(document.querySelectorAll('[data-reftool]'), function (b) {
            on(b, 'click', function () {
                if (!S) return;
                S.refTool = S.refTool === b.dataset.reftool ? null : b.dataset.reftool;
                Array.prototype.forEach.call(document.querySelectorAll('[data-reftool]'), function (x) { x.classList.toggle('on', x.dataset.reftool === S.refTool); });
            });
        });
        on($('prRefClear'), 'click', function () { if (!S) return; S.ref = null; show($('prRefBox'), false); S.segReady = null; segPrepare(); });

        on($('prVarAdd'), 'click', function () {
            var o = active(); if (!o || !o.colour || !S) return;
            if (S.variants.length >= MAX_VARIANTS) { toast(fmt(T.var_max, { n: MAX_VARIANTS }), 'info'); return; }
            var hex = root.OpColor.rgbToHex(o.colour.rgb);
            if (!S.variants.some(function (c) { return root.OpColor.rgbToHex(c) === hex; })) S.variants.push(o.colour.rgb.slice());
            renderVariants();
        });
        on($('prVarGo'), 'click', saveVariants);
        on($('prSave'), 'click', save);
        on($('prFormat'), 'change', function () { show($('prQualityRow'), this.value !== 'image/png'); });
        if (root.__toolSettings) {
            try { root.__toolSettings.bind(SLUG, $('prFormat'), 'format', 'auto'); root.__toolSettings.bind(SLUG, $('prQuality'), 'quality', '92'); } catch (_) { }
        }
        on($('prQuality'), 'input', function () { $('prQualityVal').textContent = this.value + '%'; });
    }

    function init(cfg) {
        CFG = cfg; SLUG = cfg.slug; T = cfg.t || {};
        buildSwatches();
        wire();
        stage('open');
    }

    root.PhotoRecolorApp = { init: init, _state: function () { return S; } };
})(window);
